import request from '../utils/request'
import axios from 'axios'

/**
 * 获取客户列表（管理员端，只能查看自己企业的客户）
 */
export function getCustomerList(params) {
  return request({
    url: '/manager/customer/list',
    method: 'get',
    params
  })
}

/**
 * 获取客户详情（管理员端）
 */
export function getCustomerDetail(customerId) {
  return request({
    url: '/manager/customer/detail',
    method: 'get',
    params: { customerId }
  })
}

/**
 * 获取客户总数（管理员端，统计自己企业的客户）
 */
export function getCustomerCount() {
  return request({
    url: '/manager/customer/count',
    method: 'get'
  })
}

/**
 * 获取客户意向等级字典列表
 */
export function getIntentionLevels() {
  return request({
    url: '/manager/customer/intention-levels',
    method: 'get'
  })
}

export function getManagerCustomerCrmMeta() {
  return request({
    url: '/manager/customer/crm-meta',
    method: 'get'
  })
}

/**
 * 获取客户跟进记录列表（管理员端，支持分页和多条件筛选）
 */
export function getCustomerFollowList(params) {
  return request({
    url: '/manager/customer/follow/list',
    method: 'get',
    params
  })
}

/** 客户操作日志（公海/归属/资料变更等，管理员端） */
export function getManagerCustomerOperationLogs(customerId) {
  return request({
    url: '/manager/customer/operation-logs',
    method: 'get',
    params: { customerId }
  })
}

/**
 * 导出客户列表为Excel（管理员端）
 * 使用独立的axios实例，避免响应拦截器的业务逻辑干扰
 */
/** 公海客户列表（管理员，与 list 相同接口，附加 publicSeaOnly） */
export function getPublicSeaCustomerList(params) {
  return request({
    url: '/manager/customer/list',
    method: 'get',
    params: { ...params, publicSeaOnly: true }
  })
}

export function setPublicSeaCustomerEnabled(customerId, enabled) {
  return request({
    url: '/manager/customer/public-sea/enabled',
    method: 'put',
    params: { customerId, enabled }
  })
}

export function downloadPublicSeaImportTemplate() {
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  const token = localStorage.getItem('accessToken')
  return axios({
    url: `${baseURL}/manager/customer/public-sea/import-template`,
    method: 'get',
    responseType: 'blob',
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    },
    timeout: 30000
  }).then((response) => {
    const contentType = response.headers['content-type'] || response.headers['Content-Type'] || ''
    if (contentType.includes('application/json')) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result)
            reject(new Error(errorData.msg || errorData.message || '下载失败'))
          } catch (e) {
            reject(new Error('下载失败'))
          }
        }
        reader.readAsText(response.data)
      })
    }
    return response.data
  })
}

export function importPublicSeaCustomerList(file) {
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  const token = localStorage.getItem('accessToken')
  const formData = new FormData()
  formData.append('file', file)
  return axios({
    url: `${baseURL}/manager/customer/public-sea/import`,
    method: 'post',
    data: formData,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'multipart/form-data'
    },
    timeout: 120000
  })
}

export function exportPublicSeaCustomerList(params) {
  return exportCustomerList({ ...params, publicSeaOnly: true })
}

export function exportCustomerList(params) {
  // 创建独立的axios实例用于导出
  const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
  const token = localStorage.getItem('accessToken')
  
  return axios({
    url: `${baseURL}/manager/customer/export`,
    method: 'get',
    params,
    responseType: 'blob',
    headers: {
      'Authorization': token ? `Bearer ${token}` : ''
    },
    timeout: 60000 // 导出可能需要较长时间，设置60秒超时
  }).then(response => {
    // 检查Content-Type，如果是JSON，说明是错误响应
    const contentType = response.headers['content-type'] || response.headers['Content-Type'] || ''
    if (contentType.includes('application/json')) {
      // 错误响应被转换为blob，需要解析
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result)
            reject(new Error(errorData.msg || errorData.message || '导出失败'))
          } catch (e) {
            reject(new Error('导出失败：服务器返回了无效的响应'))
          }
        }
        reader.onerror = () => reject(new Error('导出失败：无法读取服务器响应'))
        reader.readAsText(response.data)
      })
    }
    return response.data
  }).catch(error => {
    // 处理HTTP错误响应
    if (error.response) {
      const { data, status, headers } = error.response
      const contentType = headers['content-type'] || headers['Content-Type'] || ''
      
      // 如果响应是Blob类型，尝试解析为JSON
      if (data instanceof Blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            try {
              const text = reader.result
              // 尝试解析为JSON
              const errorData = JSON.parse(text)
              reject(new Error(errorData.msg || errorData.message || '导出失败'))
            } catch (e) {
              // 如果不是JSON，检查Content-Type
              if (contentType.includes('application/json')) {
                reject(new Error('导出失败：无法解析服务器响应'))
              } else {
                reject(new Error(`导出失败：HTTP ${status}`))
              }
            }
          }
          reader.onerror = () => reject(new Error('导出失败：无法读取服务器响应'))
          reader.readAsText(data)
        })
      }
      
      // 如果响应是JSON格式
      if (contentType.includes('application/json')) {
        const errorMsg = data.msg || data.message || `导出失败：HTTP ${status}`
        return Promise.reject(new Error(errorMsg))
      }
    }
    
    // 处理网络错误或其他错误
    if (error.message && error.message.includes('Network Error')) {
      return Promise.reject(new Error('网络错误，请检查网络连接'))
    }
    
    throw error
  })
}

